import { useMemo } from 'react';
import { MapContainer, TileLayer, Polygon, useMap } from 'react-leaflet';
// @ts-expect-error -- leaflet lacks bundled types
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

interface JuthoorLandPolygonMapProps {
  /** Points in NetZero format: [longitude, latitude] */
  points: [number, number][];
  landName?: string;
  /** Height of the map container. Defaults to 260px */
  height?: number;
}

function FitPolygon({ positions }: { positions: [number, number][] }) {
  const map = useMap();
  if (positions.length > 0) {
    map.fitBounds(L.latLngBounds(positions), { padding: [20, 20], maxZoom: 14 });
  }
  return null;
}

/**
 * Renders a Leaflet map with a filled polygon from NetZero land coordinates.
 * NetZero returns points as [lng, lat]; Leaflet expects [lat, lng] — swapped here.
 */
export function JuthoorLandPolygonMap({
  points,
  landName,
  height = 260,
}: JuthoorLandPolygonMapProps) {
  // Swap [lng, lat] → [lat, lng] for Leaflet
  const latLngPositions = useMemo<[number, number][]>(
    () => points.map(([lng, lat]) => [lat, lng]),
    [points]
  );

  const center = useMemo<[number, number]>(() => {
    if (latLngPositions.length === 0) return [24.7136, 46.6753];
    const sumLat = latLngPositions.reduce((s, [lat]) => s + lat, 0);
    const sumLng = latLngPositions.reduce((s, [, lng]) => s + lng, 0);
    return [sumLat / latLngPositions.length, sumLng / latLngPositions.length];
  }, [latLngPositions]);

  if (latLngPositions.length < 3) return null;

  return (
    <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700" style={{ height }}>
      <MapContainer
        {...({
          center,
          zoom: 11,
          className: 'h-full w-full z-0',
          scrollWheelZoom: false,
          zoomControl: true,
        } as any)}
      >
        <TileLayer
          {...({
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
            url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
          } as any)}
        />
        <FitPolygon positions={latLngPositions} />
        <Polygon
          positions={latLngPositions}
          pathOptions={{
            color: '#16a34a',
            fillColor: '#16a34a',
            fillOpacity: 0.2,
            weight: 2,
          }}
        />
      </MapContainer>
    </div>
  );
}
